import { capitalize } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { pluralizeWithCount } from "../../../../common/helpers/string";
import { InvoiceQueryParams } from "../../common/contracts/invoice.contract";
import {
    InvoiceStats as InvoiceStatsType,
    InvoiceStatus,
    InvoiceStatSubsegment,
    InvoiceDueDateSubsegment,
    InvoiceDueDateLabels,
} from "../../common/types/invoice";
import { SegmentBar } from "../general/SegmentBar/SegmentBar";
import { GradientColors, Segment } from "../../helpers/segment";
import styles from "./InvoiceStats.module.scss";
import { InvoiceStatTooltip } from "./InvoiceStatTooltip";

interface Props {
    invoiceStats?: InvoiceStatsType;
    showConnectOverlay?: boolean;
    filters: InvoiceQueryParams["filters"];
    setFilters: (filters: InvoiceQueryParams["filters"]) => void;
}

const statusGradientColors: Record<keyof InvoiceStatsType, GradientColors> = {
    draft: {
        start: "#FAFBFC",
        end: "#D9DDE2",
        subtitle: "#515D71",
    },
    open: {
        start: "#FAE9D9",
        end: "#F9CAA1",
        subtitle: "#A86534",
    },
    paid: {
        start: "#E2F0FB",
        end: "#94C9F1",
        subtitle: "#115F9E",
    },
    void: {
        start: "#EBE5FD",
        end: "#B8A4F1",
        subtitle: "#5B40AB",
    },
    uncollectable: {
        start: "#fce5e7",
        end: "#faa4ab",
        subtitle: "#710006",
    },
};

export const InvoiceStats: React.FC<Props> = ({
    invoiceStats,
    filters,
    setFilters,
}) => {
    const [stats, setStats] = useState<InvoiceStatsType | null>(
        invoiceStats ?? null,
    );

    // HACK: this is a hack to prevent major re-renders when the query changes
    useEffect(() => {
        if (!invoiceStats) {
            return;
        }
        setStats(invoiceStats);
    }, [invoiceStats]);

    const segments = useMemo(() => {
        if (!stats) {
            return [];
        }

        return Object.entries(stats).map(([id, stat]) => {
            const baseSegment: Segment = {
                id,
                label: capitalize(id),
                value: stat.total,
                colors: statusGradientColors[id as keyof InvoiceStatsType],
                subtitle: pluralizeWithCount("invoice", stat.count),
                subsegments: stat.subsegments?.map(
                    (subsegment: InvoiceStatSubsegment) => ({
                        id: subsegment.label,
                        label: InvoiceDueDateLabels[subsegment.label],
                        value: subsegment.total,
                        subtitle: pluralizeWithCount(
                            "invoice",
                            subsegment.count,
                        ),
                        tooltip: (
                            <InvoiceStatTooltip
                                status={id as InvoiceStatus}
                                label={InvoiceDueDateLabels[subsegment.label]}
                                stat={subsegment}
                                entityStats={subsegment.entities}
                            />
                        ),
                    }),
                ),
                tooltip: (
                    <InvoiceStatTooltip
                        status={id as InvoiceStatus}
                        stat={stat}
                    />
                ),
            };

            return baseSegment;
        });
    }, [stats]);

    const filterByDueDateSubsegment = useCallback(
        (subsegment: InvoiceDueDateSubsegment) => {
            const newFilters: InvoiceQueryParams["filters"] = {
                ...filters,
            };

            // Set due date range based on the subsegment
            switch (subsegment) {
                case InvoiceDueDateSubsegment.NOT_YET_DUE:
                    newFilters.overdue = false;
                    newFilters.minOverdue = undefined;
                    newFilters.maxOverdue = undefined;
                    break;
                case InvoiceDueDateSubsegment.PAST_DUE_1_TO_30:
                    newFilters.overdue = true;
                    newFilters.minOverdue = 1;
                    newFilters.maxOverdue = 30;
                    break;
                case InvoiceDueDateSubsegment.PAST_DUE_31_TO_60:
                    newFilters.overdue = true;
                    newFilters.minOverdue = 31;
                    newFilters.maxOverdue = 60;
                    break;
                case InvoiceDueDateSubsegment.PAST_DUE_61_PLUS:
                    newFilters.overdue = true;
                    newFilters.minOverdue = 61;
                    newFilters.maxOverdue = undefined;
                    break;
            }

            setFilters(newFilters);
        },
        [filters, setFilters],
    );

    const filterByStatus = useCallback(
        (status: InvoiceStatus) => {
            const newFilters: InvoiceQueryParams["filters"] = {
                ...filters,
            };

            newFilters.invoiceStatuses = [status];
            setFilters(newFilters);
        },
        [filters, setFilters],
    );

    const onSegmentClick = useCallback(
        (segmentId: string) => {
            if (
                Object.values(InvoiceDueDateSubsegment).includes(
                    segmentId as InvoiceDueDateSubsegment,
                )
            ) {
                filterByDueDateSubsegment(
                    segmentId as InvoiceDueDateSubsegment,
                );
            } else if (
                Object.values(InvoiceStatus).includes(
                    segmentId as InvoiceStatus,
                )
            ) {
                filterByStatus(segmentId as InvoiceStatus);
            }
        },
        [filterByDueDateSubsegment, filterByStatus],
    );

    return (
        <SegmentBar
            className={styles.invoiceStats}
            segments={segments}
            formatValueAsMoney
            onSegmentClick={onSegmentClick}
        />
    );
};
